import { useCallback, useEffect, useRef, useState } from 'react';
import { playMiniTaskSound } from '../../soundEffects';

const SELECTOR_DELAYS = [55, 60, 66, 74, 84, 96, 110, 126, 145, 168, 195, 226, 262, 304, 352, 411, 266];

export default function useRandomStudentSelector(students, soundEnabled) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [statusText, setStatusText] = useState('Kartlar karışıyor...');
  const [previousStudent, setPreviousStudent] = useState(null);
  const [displayStudent, setDisplayStudent] = useState(null);
  const [nextStudent, setNextStudent] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [animationStep, setAnimationStep] = useState(0);
  const timersRef = useRef([]);
  const runIdRef = useRef(0);
  const displayStudentRef = useRef(null);
  const nextStudentRef = useRef(null);
  const selectedIdsRef = useRef(new Set());
  const lastSelectedIdRef = useRef(null);
  const runningRef = useRef(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current = [];
  }, []);

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  const pickRandomStudent = useCallback((studentList) => {
    if (!studentList?.length) return null;
    return studentList[Math.floor(Math.random() * studentList.length)];
  }, []);

  const run = useCallback(() => {
    if (!students.length || runningRef.current) return;

    clearTimers();
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    let candidates = students.filter((student) => !selectedIdsRef.current.has(String(student.id)));
    if (!candidates.length) {
      selectedIdsRef.current.clear();
      candidates = students.filter((student) => String(student.id) !== lastSelectedIdRef.current);
    }
    const finalStudent = pickRandomStudent(candidates.length ? candidates : students);
    const initialStudent = pickRandomStudent(students);
    const initialNextStudent = pickRandomStudent(students);
    let elapsed = 0;

    setOpen(true);
    runningRef.current = true;
    setRunning(true);
    setSelectedStudent(null);
    setStatusText('Kartlar karışıyor...');
    setPreviousStudent(pickRandomStudent(students));
    setDisplayStudent(initialStudent);
    setNextStudent(initialNextStudent);
    setAnimationStep(0);
    displayStudentRef.current = initialStudent;
    nextStudentRef.current = initialNextStudent;

    SELECTOR_DELAYS.forEach((delay, index) => {
      elapsed += delay;
      const timer = setTimeout(() => {
        if (runIdRef.current !== runId) return;

        const isFinalStep = index === SELECTOR_DELAYS.length - 1;
        setAnimationStep(index + 1);
        if (index >= Math.floor(SELECTOR_DELAYS.length / 2)) {
          setStatusText('Sıradaki öğrenci seçiliyor...');
        }

        if (isFinalStep) {
          setPreviousStudent(displayStudentRef.current);
          setDisplayStudent(finalStudent);
          setNextStudent(nextStudentRef.current);
          setSelectedStudent(finalStudent);
          selectedIdsRef.current.add(String(finalStudent.id));
          lastSelectedIdRef.current = String(finalStudent.id);
          runningRef.current = false;
          setRunning(false);
          setStatusText(`Sıra ${finalStudent?.display_name || 'Öğrenci'}’te!`);
          displayStudentRef.current = finalStudent;
          playMiniTaskSound('complete', soundEnabled);
          return;
        }

        const upcomingStudent = pickRandomStudent(students);
        setPreviousStudent(displayStudentRef.current);
        setDisplayStudent(nextStudentRef.current);
        setNextStudent(upcomingStudent);
        displayStudentRef.current = nextStudentRef.current;
        nextStudentRef.current = upcomingStudent;
      }, elapsed);
      timersRef.current.push(timer);
    });
  }, [clearTimers, pickRandomStudent, soundEnabled, students]);

  const close = useCallback(() => {
    runIdRef.current += 1;
    clearTimers();
    selectedIdsRef.current.clear();
    lastSelectedIdRef.current = null;
    runningRef.current = false;
    setOpen(false);
    setRunning(false);
  }, [clearTimers]);

  return {
    open,
    running,
    statusText,
    previousStudent,
    displayStudent,
    nextStudent,
    selectedStudent,
    animationStep,
    run,
    close,
  };
}
